import React, { useState, useEffect, useCallback } from "react";
import api from "../services/api";
import { useParams } from "react-router-dom";
import { BsFiletypePdf } from "react-icons/bs";
import "./PayList.css";

const PayList = () => {
  const [payrollData, setPayrollData] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { id: businessId } = useParams();

  const [year, setYear] = useState("");
  const [month, setMonth] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [qYear, setQYear] = useState("");
  const [quarter, setQuarter] = useState("");


  // Memoized function to fetch payroll data
  const fetchPayrollData = useCallback(
    async (startDate, endDate) => {
      try {
        setLoading(true);
        const response = await api.post(
          `/payroll/filter/${businessId}`,
          { startDate, endDate }
        );

        setTotals(response.data.totals);
        setPayrollData(
          response.data.records.sort((a, b) => a.EMPLOYEE_ID - b.EMPLOYEE_ID)
        );
        setError(null);
      } catch (err) {
        setError(err.response?.data?.message || "An error occurred.");
      } finally {
        setLoading(false);
      }
    },
    [businessId]
  );

  // Automatically fetch data when year and month are selected
  useEffect(() => {
    if (year && month) {
      const firstDay = `${year}-${month.toString().padStart(2, "0")}-01`;
      const lastDay = new Date(year, month, 0).toISOString().split("T")[0];
      setStartDate(firstDay);
      setEndDate(lastDay);
      fetchPayrollData(firstDay, lastDay);
    }
  }, [year, month, fetchPayrollData]);

  // Handle form submission for custom dates
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!startDate || !endDate) {
      setError("Start and End dates are required.");
      return;
    }
    fetchPayrollData(startDate, endDate);
  };


  const handleDownload941PDF = async () => {
  if (!qYear || !quarter) {
    alert("Please select year and quarter.");
    return;
  }

  try {
    const response = await api.post(
      `/payroll/941/pdf/${businessId}`,
      { year: qYear, quarter },
      { responseType: "blob" }
    );

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `form941_${businessId}_${qYear}_Q${quarter}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  } catch (error) {
    console.error("Error downloading 941 PDF:", error);
    alert("Failed to download Form 941 PDF.");
  }
};


  const handleDownloadPDF = async () => {
    try {
      const response = await api.post(
        `/payroll/pdf/${businessId}`,
        { startDate, endDate },
        { responseType: 'blob' } // Ensure the response is treated as a file
      );
  
      // Create a URL for the PDF blob
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `payroll_${businessId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Failed to download PDF.');
    }
  };


  const handleDownloadW2PDF = async () => {
  try {
    if (!year) {
      alert("Please select a year for W-2s.");
      return;
    }

    const response = await api.post(
      `/payroll/w2/pdf/${businessId}`,
      { year },
      { responseType: "blob" }
    );

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `w2_${businessId}_${year}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  } catch (error) {
    console.error("Error downloading W-2 PDF:", error);
    alert("Failed to download W-2 PDF.");
  }
};


  // Group payroll data by employee and calculate totals for each employee
  const groupedData = payrollData.reduce((acc, record) => {
    if (!acc[record.NAME]) {
      acc[record.NAME] = {
        records: [],
        totals: {
          grossPay: 0,
          netPay: 0,
          federalTax: 0,
          stateTax: 0,
          fedSS: 0,
          fedMed: 0,
          stateUi: 0,
          stateUiTaxable: 0,
          fedUiTaxable: 0,
        },
      };
    }
    acc[record.NAME].records.push(record);
    acc[record.NAME].totals.grossPay += parseFloat(record.GROSSPAY || 0);
    acc[record.NAME].totals.netPay += parseFloat(record.NET_PAY || 0);
    acc[record.NAME].totals.federalTax += parseFloat(record.FED_TAX || 0);
    acc[record.NAME].totals.stateTax += parseFloat(record.STATE_TAX || 0);
    acc[record.NAME].totals.fedSS += parseFloat(record.FED_SS || 0);
    acc[record.NAME].totals.fedMed += parseFloat(record.FED_MED || 0);
    acc[record.NAME].totals.stateUi += parseFloat(record.STATE_UI || 0);
    acc[record.NAME].totals.stateUiTaxable += parseFloat(record.STATE_UI_TAXABLE || 0);
    acc[record.NAME].totals.fedUiTaxable += parseFloat(record.TAXABLE_WAGES || 0);
    return acc;
  }, {});

  // Generate years and months
  const generateYears = () => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 10 }, (_, i) => currentYear - i);
  };
  

  const generateMonths = () => {
    return Array.from({ length: 12 }, (_, i) => i + 1);
  };

  if (loading) return <div className="loading">Loading...</div>;
  if (error) return <div className="error">Error: {error}</div>;

  return (
    <div className="paylist-container">

      {/* Filters */}
      <form onSubmit={handleSubmit} className="filter-form">
  <div className="filter-group">
    <h4 className="filter-heading">Payroll Filters</h4>

    <label>
      Year:
      <select value={year} onChange={(e) => setYear(e.target.value)}>
        <option value="">Select Year</option>
        {generateYears().map((yr) => (
          <option key={yr} value={yr}>
            {yr}
          </option>
        ))}
      </select>
    </label>

    <label>
      Month:
      <select value={month} onChange={(e) => setMonth(e.target.value)}>
        <option value="">Select Month</option>
        {generateMonths().map((mo) => (
          <option key={mo} value={mo}>
            {mo.toString().padStart(2, "0")}
          </option>
        ))}
      </select>
    </label>

    <label>
      Start Date:
      <input
        type="date"
        value={startDate}
        onChange={(e) => setStartDate(e.target.value)}
      />
    </label>

    <label>
      End Date:
      <input
        type="date"
        value={endDate}
        onChange={(e) => setEndDate(e.target.value)}
      />
    </label>

    <button type="submit" className="filter-btn">
      Apply Filters
    </button>

    <button
      type="button"
      className="pdf-btn"
      onClick={handleDownloadPDF}
      title="Download Payroll PDF"
    >
      <BsFiletypePdf />
    </button>

    <button
      type="button"
      className="secondary-btn"
      onClick={handleDownloadW2PDF}
      title="Download W-2 Forms"
    >
      W-2
    </button>
  </div>

  <div className="filter-group">
    <h4 className="filter-heading">Quarterly Forms</h4>

    <label>
      941 Year:
      <select value={qYear} onChange={(e) => setQYear(e.target.value)}>
        <option value="">Select Year</option>
        {generateYears().map((yr) => (
          <option key={yr} value={yr}>
            {yr}
          </option>
        ))}
      </select>
    </label>

    <label>
      Quarter:
      <select value={quarter} onChange={(e) => setQuarter(e.target.value)}>
        <option value="">Select Quarter</option>
        <option value="1">Q1</option>
        <option value="2">Q2</option>
        <option value="3">Q3</option>
        <option value="4">Q4</option>
      </select>
    </label>

    <button
      type="button"
      className="pdf-btn"
      onClick={handleDownload941PDF}
      title="Download Form 941 PDF"
    >
      <BsFiletypePdf />
    </button>
  </div>
</form>

      {/* Totals */}
      {totals && (
        <div className="totals">
          <h2>Totals</h2>
          <div className="totals-row">
            <p><strong>Total Gross Pay:</strong> ${totals.totalGrossPay}</p>
            <p><strong>Total Net Pay:</strong> ${totals.totalNetPay}</p>
            <p><strong>Total Federal Tax:</strong> ${totals.totalFedTax}</p>
            <p><strong>Total State Tax:</strong> ${totals.totalStateTax}</p>
            <p><strong>Total Social Security:</strong> ${totals.totalFedSS}</p>
            <p><strong>Total Medicare:</strong> ${totals.totalFedMed}</p>
            <p><strong>Total State UI:</strong> ${totals.totalStateUi}</p>
            <p><strong>Total UI Taxable:</strong> ${totals.totalStateUiTaxable}</p>
            <p><strong>Federal UI Taxable:</strong> ${totals.totalFedUiTaxable}</p>
          </div>
        </div>
      )}

      {/* Payroll Records */}
      <div className="payroll-records">
        {Object.keys(groupedData).length === 0 ? (
          <p>No payroll records found for the selected criteria.</p>
        ) : (
          Object.entries(groupedData).map(([employeeName, { records, totals }]) => (
            <div key={employeeName} className="employee-record">
              <h3>{employeeName}</h3>
              <div className="employee-totals">
                <p><strong>Total Gross Pay:</strong> ${totals.grossPay.toFixed(2)}</p>
                <p><strong>Total Net Pay:</strong> ${totals.netPay.toFixed(2)}</p>
                <p><strong>Total Federal Tax:</strong> ${totals.federalTax.toFixed(2)}</p>
                <p><strong>Total State Tax:</strong> ${totals.stateTax.toFixed(2)}</p>
              </div>
              <table>
                <thead>
                  <tr>
                    <th>Pay Date</th>
                    <th>Gross Pay</th>
                    <th>Net Pay</th>
                    <th>Federal Tax</th>
                    <th>State Tax</th>
                    <th>Fed SS</th>
                    <th>Fed Med</th>
                  </tr>
                </thead>
                <tbody className="payResults">
                  {records.map((record, index) => (
                    <tr key={index}>
                      <td>{new Date(record.PAY_DATE).toLocaleDateString("en-US")}</td>
                      <td>{record.GROSSPAY}</td>
                      <td>{record.NET_PAY}</td>
                      <td>{record.FED_TAX}</td>
                      <td>{record.STATE_TAX}</td>
                      <td>{record.FED_SS}</td>
                      <td>{record.FED_MED}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default PayList;


